import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewEncapsulation, computed } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, interval } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { PaymentService } from '../services/payment.service';
import { MsalHelperService } from '../services/msal-helper.service';
import { ApiService } from '../services/api.service';
import { ContextService } from '../services/context.service';
import { distinct, groupBy } from '../services/flow-management.service';
import { FlowRun } from '../obj/FlowRun';
import { DialogCloseResult, DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { DetailsComponent } from './details.component';

export interface TeamsMonitorSummary {
  totalRuns: number;
  successRate: number;
  failedRuns: number;
  monitoredFlows: number;
  ruleCount: number;
}

export function buildTeamsMonitorSummary(runs: any[], monitoredFlows: number, ruleCount: number): TeamsMonitorSummary {
  const succeededRuns = (runs || []).filter((run) => `${run?.status || ''}`.toLowerCase() === 'succeeded').length;
  const failedRuns = (runs || []).filter((run) => `${run?.status || ''}`.toLowerCase() === 'failed').length;
  const completedRuns = succeededRuns + failedRuns;
  return {
    totalRuns: (runs || []).length,
    successRate: completedRuns ? succeededRuns / completedRuns : 0,
    failedRuns,
    monitoredFlows,
    ruleCount
  };
}

@Component({
    selector: 'app-teams-dashboard',
    templateUrl: './teams-dashboard.component.html',
    styleUrls: ['./teams-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class TeamsDashboardComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private destroy$ = new Subject<void>();

  public license$ = new BehaviorSubject<any>(null);
  public user$ = new BehaviorSubject<any>(null);
  public scans$ = new BehaviorSubject<any[]>([]);
  public scansTimespan$: Observable<any>;
  public accounts$ = new BehaviorSubject<any[]>([]);
  public flows$ = new BehaviorSubject<any[]>([]);
  public monitored$ = new BehaviorSubject<any[]>([]);
  public contextState: 'loading' | 'noStorage' | 'noLicense' | 'ready' = 'loading';
  public flowChartData$ = new BehaviorSubject<any[]>([]);
  public runs$: BehaviorSubject<FlowRun[]> = new BehaviorSubject([]);
  public runChartData$ = new BehaviorSubject<any[]>([]);
  public periodStart$ = new BehaviorSubject<Date>(null);
  public periodEnd$ = new BehaviorSubject<Date>(null);
  public rules$ = new BehaviorSubject<any[]>([]);
  public summary = {
    totalRuns: 0,
    successRate: 0,
    failedRuns: 0,
    monitoredFlows: 0,
    ruleCount: 0
  };
  public flowFilter: 'monitored' | 'all' = 'monitored';
  public tableView: 'custom' | 'grid' = 'grid';
  public gridSearch$ = new BehaviorSubject<string>('');
  public gridSearchTerm = '';
  public currentPage = 0;
  public readonly PAGE_SIZE = 25;
  public workspace;
  public workspaceFlowCapacity;

  constructor(
    private paymentService: PaymentService,
    private msalHelperService: MsalHelperService,
    private monitorService: ApiService,
    private route: ActivatedRoute,
    private contextService: ContextService,
    private dialogService: DialogService
  ) {
    this.workspace = this.paymentService.workspace;
    this.workspaceFlowCapacity = computed(() =>
      (this.workspace() || []).reduce((sum, ws) => sum + ws.flow_capacity, 0)
    );
    this.subscription.add(
      route.queryParamMap.subscribe((params) => {
        const op = params.get('op');
        const table = params.get('table');

        if (op && table) {
          this.downloadCsv(table);
        }
      })
    );
  }

  ngOnInit(): void {
    this.subscription.add(
      this.msalHelperService.user$.subscribe((user) => {
        if (user) this.user$.next(user);
      })
    );

    this.contextService.load().then((ctx) => {
      if (!ctx?.workspace) {
        this.contextState = 'noLicense';
        return;
      }
      if (!ctx.hasWorkspaceStorage) {
        this.contextState = 'noStorage';
        return;
      }
      if (!ctx.hasTeamsEntitlement) {
        this.contextState = 'noLicense';
        return;
      }

      this.license$.next(ctx.clarityUser);
      this.accounts$.next(ctx.accounts);
      this.contextState = 'ready';

      this.monitorService.watchConfigurations().then((query) => {
        this.subscription.add(
          query.subscribe((result) => {
            const configs = result.data;
            const rules = configs?.rules || [];
            this.rules$.next(rules);
            this.summary = { ...this.summary, ruleCount: rules.length };
          })
        );
      });

      this.subscription.add(
        this.monitorService.run$.subscribe((runs) => {
          if (runs === null) {
            this.monitorService.watchRuns(-7).then(({ start, end }) => {
              this.periodEnd$.next(end);
              this.periodStart$.next(start);
            });
          }
        })
      );

      this.subscription.add(
        combineLatest([this.monitorService.flow$, this.monitorService.run$]).subscribe(([flows, runs]) => {
          if (flows === null || runs === null) {
            return;
          }

          this.flows$.next(flows || []);
          this.runs$.next((runs || []).map((run) => new FlowRun(run)));

          const groupedRuns = groupBy(runs, 'status');
          const groupedFlowRuns = groupBy(runs, 'flowName');
          const statuses = (runs || []).map((r) => r?.['status']).filter(distinct);

          const runChartData = statuses.map((s) => ({
            status: s,
            count: (groupedRuns[s] || []).length,
            colour: this.getColour(s)
          }));

          const monitored = flows.filter((f) => f.name && f.monitor).map((f) => {
            const flowRuns = groupedFlowRuns[f.name] || [];
            const groupedRunsByStatus = groupBy(flowRuns, 'status');

            f.periodRuns = flowRuns.length;
            f.periodSuccessed = (groupedRunsByStatus['Succeeded'] || []).length;
            f.periodFailed = (groupedRunsByStatus['Failed'] || []).length;
            f.periodCancelled = (groupedRunsByStatus['Cancelled'] || []).length;

            f.subState = f.state;
            if (f.periodRuns == 0) {
              f.subState = `${f.state}:No Runs`;
              return f;
            }

            const success = f.periodSuccessed / f.periodRuns;
            f.periodFailedRate = f.periodFailed / f.periodRuns;

            switch (true) {
              case (success > 0.95):
                f.subState = `${f.state}:Healthy`;
                break;
              case (success > 0.60):
                f.subState = `${f.state}:Warning`;
                break;
              default:
                f.subState = `${f.state}:Critical`;
                break;
            }
            return f;
          });

          this.monitored$.next(monitored);
          this.summary = buildTeamsMonitorSummary(runs || [], monitored.length, this.summary.ruleCount);

          const groupedFlows1 = groupBy(monitored, 'subState');
          const states = (flows || []).map((f) => f.subState).filter(distinct);
          const chartFlowData = states.map((s) => ({
            state: s,
            count: (groupedFlows1[s] || []).length,
            colour: this.getColour(s)
          }));

          this.flowChartData$.next(chartFlowData);
          this.runChartData$.next(runChartData);
        })
      );

      this.refreshStats();

      this.scansTimespan$ = combineLatest([interval(1000), this.scans$]).pipe(map(([t, scans]) => {
        return (scans || []).map((stats) => {
          try {
            const start = new Date(stats.start).valueOf();
            const end = stats.ended ? new Date(stats.end).valueOf() : new Date().valueOf();
            const diffInMs = Math.abs(end - start);
            const diffInMins = Math.floor(diffInMs / 1000 / 60);
            const diffInSecs = Math.floor((diffInMs / 1000) % 60);

            return `${diffInMins} minutes and ${diffInSecs} seconds`;
          } catch (error) {
            return `... minutes and ... seconds`;
          }
        });
      }));
      this.subscription.add(this.scansTimespan$.subscribe());
    }).catch(() => {
      this.contextState = 'noLicense';
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public refreshStats(): void {
    this.monitorService.queryScans().then((scans) => {
      this.scans$.next(scans);

      if (!scans) {
        setTimeout(() => {
          this.refreshStats();
        }, 30000);
      }
    });
  }

  public findAccount(oid) {
    return this.accounts$.value.find((a) => a.oid == oid);
  }

  public encodeState(state, cds = null) {
    const { workspace } = this.license$.getValue();

    const o = {
      s: state,
      ws: workspace,
      cds,
      r: `${window.location.href}`
    };

    return btoa(JSON.stringify(o));
  }

  public labelContent(e): string {
    return `${e.value} (${(e.percentage * 100).toFixed(2)}%)`;
  }

  public downloadCsv(table = 'flowruns'): void {
    this.monitorService.downloadCsv(table);
  }

  public getFlowRows(): any[] {
    const all = this.flowFilter === 'monitored'
      ? (this.monitored$.value || [])
      : (this.flows$.value || []);
    const term = this.gridSearchTerm.toLowerCase().trim();
    const filtered = term
      ? all.filter(f =>
          (f.displayName || '').toLowerCase().includes(term) ||
          (f.name || '').toLowerCase().includes(term) ||
          (f.ref_environment?.displayName || '').toLowerCase().includes(term)
        )
      : all;
    const start = this.currentPage * this.PAGE_SIZE;
    return filtered.slice(start, start + this.PAGE_SIZE);
  }

  public getTotalRows(): number {
    const all = this.flowFilter === 'monitored'
      ? (this.monitored$.value || [])
      : (this.flows$.value || []);
    const term = this.gridSearchTerm.toLowerCase().trim();
    return term
      ? all.filter(f =>
          (f.displayName || '').toLowerCase().includes(term) ||
          (f.name || '').toLowerCase().includes(term) ||
          (f.ref_environment?.displayName || '').toLowerCase().includes(term)
        ).length
      : all.length;
  }

  public getTotalPages(): number {
    return Math.ceil(this.getTotalRows() / this.PAGE_SIZE) || 1;
  }

  public prevPage(): void {
    if (this.currentPage > 0) this.currentPage--;
  }

  public nextPage(): void {
    if (this.currentPage < this.getTotalPages() - 1) this.currentPage++;
  }

  public setPage(p: number): void {
    this.currentPage = p;
  }

  public getPageRange(): number[] {
    const total = this.getTotalPages();
    const cur = this.currentPage;
    const start = Math.max(0, Math.min(cur - 1, total - 3));
    const end = Math.min(total, start + 3);
    return Array.from({ length: end - start }, (_, i) => start + i);
  }

  public setFlowFilter(filter: 'monitored' | 'all'): void {
    this.flowFilter = filter;
    this.currentPage = 0;
  }

  public getOwnerLabel(flow: any): string {
    const createdBy = flow?.ref_createdBy?.displayName;
    if (createdBy) return createdBy;
    const owner = flow?.owners?.[0];
    return owner?.displayName || owner?.name || 'Unknown';
  }

  public getRunsLabel(flow: any): string {
    return `${flow?.periodRuns ?? flow?.runTotal ?? 0}`;
  }

  public getTagsLabel(flow: any): string {
    const raw = flow?.tags;
    if (!raw) return '';
    // tags stored as comma-separated string from GraphQL
    const parts = String(raw).split(',').map((t: string) => t.trim()).filter(Boolean);
    return parts.slice(0, 3).join(' · ');
  }

  public getTriggerLabel(flow: any): string {
    return flow?.triggerType || flow?.triggerKind || 'Unknown';
  }

  public getTriggerIconClass(flow: any): string {
    const t = (flow?.triggerType || flow?.triggerKind || '').toLowerCase();
    if (t.includes('recurrence') || t.includes('schedule')) return 'recurrence';
    if (t.includes('request') || t.includes('manual') || t.includes('button')) return 'request';
    if (t.includes('webhook')) return 'webhook';
    return 'automated';
  }

  public getLastModifiedLabel(flow: any): string {
    const value = flow?.modified || flow?.lastModifiedTime;
    return value ? `${new Date(value).toLocaleString()}` : 'Unknown';
  }

  public getSuccessRateLabel(flow: any): string {
    const rate = flow?.periodSuccessed != null && flow?.periodRuns ? (flow.periodSuccessed / flow.periodRuns) : flow?.successRate;
    if (typeof rate === 'number') {
      return `${(rate * 100).toFixed(1)}%`;
    }
    return flow?.successRateText || 'N/A';
  }

  public async editFlow(flow: any): Promise<void> {
    const { RowKey: rowKey } = flow;
    try {
      const dialog: DialogRef = this.dialogService.open({
        title: 'Flow Rules',
        content: DetailsComponent,
        width: 508,
        height: 470,
        minWidth: 400,
      });
      const details = dialog.content.instance as DetailsComponent;
      const user = await this.msalHelperService.getUser();
      const queryResult = await this.monitorService.queryFlow(rowKey);
      const flow1 = queryResult.data?.flow;
      if (!flow1) return;
      details.flow = flow1;
      details.form.setValue({
        monitor: flow1.monitor,
        notifyOnFail: !!flow1.rule_notify_onfail,
        notifyOnMissed: (flow1.rule_notify_onmissingdays || 0) > 0,
        missingDays: flow1.rule_notify_onmissingdays || 0,
        email: flow1.rule_notify_email || user?.username
      });
      const result: any = await dialog.result.toPromise();
      if (result instanceof DialogCloseResult) return;
      this.monitorService.mutateFlow(`${flow1.RowKey}`, {
        monitor: result.monitor,
        rule_notify_onfail: !!result.notifyOnFail,
        rule_notify_onmissingdays: result.notifyOnMissed ? result.missingDays : 0,
        rule_notify_email: result.email
      }, () => {});
    } catch {}
  }

  public trackFlow(index: number, flow: any): string {
    return flow?.flowName || flow?.name || `${index}`;
  }

  public getFlowEnvironmentLabel(flow: any): string {
    return flow?.environment?.displayName || flow?.environmentName || flow?.environment || 'Production';
  }

  public getFlowVersionLabel(flow: any): string {
    const sources: string[] = Array.isArray(flow?._sources)
      ? flow._sources
      : (flow?._source ? [flow._source] : []);
    if (sources.includes('shared')) return 'Shared';
    if (sources.includes('admin')) return 'Admin';
    return 'My Flows';
  }

  public getFlowErrorCount(flow: any): number {
    return flow?.periodFailed ?? flow?.runFails ?? 0;
  }

  public getFlowStatusTone(flow: any): 'healthy' | 'warning' | 'critical' {
    const rate = flow?.periodSuccessed != null && flow?.periodRuns ? (flow.periodSuccessed / flow.periodRuns) : flow?.successRate;
    if (typeof rate !== 'number') {
      return 'warning';
    }
    if (rate >= 0.95) {
      return 'healthy';
    }
    if (rate >= 0.75) {
      return 'warning';
    }
    return 'critical';
  }

  public getSystemLogs(): Array<{ tone: 'success' | 'warning' | 'info'; title: string; detail: string; time: string }> {
    const logs = (this.runs$.value || [])
      .slice()
      .sort((left: any, right: any) => {
        const leftTime = new Date(left?.startTime || left?.start || left?.created || 0).valueOf();
        const rightTime = new Date(right?.startTime || right?.start || right?.created || 0).valueOf();
        return rightTime - leftTime;
      })
      .slice(0, 3)
      .map((run: any) => {
        const status = `${run?.status || 'Update'}`.toLowerCase();
        let tone: 'success' | 'warning' | 'info' = 'info';
        let title = 'Run Updated';

        if (status.includes('succeed')) {
          tone = 'success';
          title = 'Run succeeded';
        } else if (status.includes('fail') || status.includes('cancel')) {
          tone = 'warning';
          title = 'Run requires attention';
        } else if (status.includes('running') || status.includes('start')) {
          tone = 'info';
          title = 'Flow activity detected';
        }

        const flowName = run?.displayName || run?.flowName || run?.name || 'Unknown flow';
        const identifier = run?.name || run?.runName || run?.id || 'pending';
        const timeValue = run?.startTime || run?.start || run?.created;

        return {
          tone,
          title,
          detail: `Flow ID: ${identifier} | ${flowName}`,
          time: timeValue ? new Date(timeValue).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Live'
        };
      });

    if (logs.length) {
      return logs;
    }

    return [];
  }

  private getColour(category: any) {
    switch (category) {
      case 'Succeeded':
        return '#78d237';
      case 'Running':
        return '#ffd246';
      case 'Started:Healthy':
        return '#78d237';
      case 'Started:Warning':
        return '#ffd246';
      case 'Started:No Runs':
        return '#ffd246';
      case 'Started:Critical':
        return '#ff6358';
      case 'Stopped:No Runs':
        return '#ff6358';
      default:
        return '#ff6358';
    }
  }

}
